import React, {useCallback, useState} from 'react';
import {DataGridPro, GridColDef, GridValueFormatterParams} from '@mui/x-data-grid-pro';
import DialogComponent from 'components/Dialog';
import DaysOfWeekField from 'components/inputs/DaysOfWeekField';
import {castArray} from 'lodash';
import moment from 'moment';
import {Trans} from 'react-i18next';
import {DialogTypes} from '../../../../../../common/names';
import {dayOfWeekTemplate} from '../../../../../../common/templates';
import Button from '../../../../../../components/Button';
import FormikTextField from '../../../../../../components/inputs/FormikTextField';
import Panel from '../../../../../../components/Panel';
import i18n from '../../../../../../i18n';
import {HolidayWithId, IHoliday, IHolidayWithId} from '../../../../../../types/Holiday';
import {HolidayCalendar, IHolidayCalendar} from '../../../../../../types/HolidayCalendar';
import AddHolidayDialog from '../AddHoliday/AddHolidayDialog';
import AddUSHolidayDialog from '../AddUSHoliday/AddUSHoliday';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {WarningButton} from 'components/buttons/WarningButton';
import {formatDate} from 'common/dateUtils';
import {Grid} from '@mui/material';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';

type Props = {
   onClose: () => void;
   onSubmit: (values: IHolidayCalendar, formikBag: FormOnSubmitHelpers<IHolidayCalendar>) => void;
   calendar?: IHolidayCalendar;
};

export const holidayColumns: GridColDef[] = [
   {field: 'name', headerName: i18n.t('Name'), flex: 200},
   {
      field: 'date',
      headerName: i18n.t('Date'),
      flex: 200,
      valueFormatter: (params: GridValueFormatterParams) => formatDate(params.value as Date),
   },
   {
      field: 'dayOfWeek',
      headerName: i18n.t('Day of Week'),
      flex: 200,
      valueGetter: (params) => params.row.date,
      valueFormatter: (params: GridValueFormatterParams) => dayOfWeekTemplate(params.value as Date),
   },
];

type AddCalendarFormProps = {
   onClose: () => void;
};

const AddEditCalendarForm = (props: AddCalendarFormProps) => {
   const {onClose} = props;
   const [selected, setSelected] = useState<IHoliday>();
   const [isAddOpened, addOpen] = useState(false);
   const [isAddUSOpened, addUSOpen] = useState(false);
   const getValue = useFieldValueGetter<IHolidayCalendar>();
   const {setValue} = useFormContext<IHolidayCalendar>();

   const holidays = getValue('holidays');

   const addHoliday = useCallback(
      (holiday: IHoliday | IHoliday[]) => {
         // console.debug('Add Holiday', holiday);
         const newHolidays = castArray(holiday);
         setValue('holidays', [...(holidays || []), ...newHolidays], SetValueOptions);
         addOpen(false);
         addUSOpen(false);
         // alert('Add item');

         //TODO add server logic
         // if (gridRef && gridRef.current) {
         //   gridRef.current.addRecord(holidays);
         // }
      },
      [setValue, holidays]
   );

   const deleteHoliday = useCallback(() => {
      // alert(`delete ${this.state.selected?.id}`)
      // console.debug('delete', selected);

      setValue(
         'holidays',
         // @ts-ignore
         holidays.filter((h) => h.name !== selected.name),
         SetValueOptions
      );
   }, [setValue, selected, holidays]);

   const deletePast = () => {
      const today = moment();
      const filteredHolidays = holidays.filter((h) => moment(h.date).isSameOrAfter(today, 'day'));
      setValue('holidays', filteredHolidays, SetValueOptions);
      // alert(`deletePast`)
   };

   // DataGrid requires id field
   const holidaysWithId: IHolidayWithId[] = React.useMemo(
      () => holidays.map((h: IHoliday) => HolidayWithId.cast({id: null, ...h})),
      [holidays]
   );

   return (
      <>
         <Grid container spacing={3}>
            <Grid item sm={12} lg={6}>
               <FormikTextField name="name" placeholder={i18n.t('Calendar Name')} autoFocus />
            </Grid>
         </Grid>
         <Grid container spacing={3}>
            <Grid item sm={12} lg={8}>
               <FormikTextField name="description" placeholder={i18n.t('Description')} />
            </Grid>
         </Grid>
         <Panel name={i18n.t('Non-Business Days')}>
            <Grid container spacing={3}>
               <Grid item sm={12} lg={12}>
                  <DaysOfWeekField name="nonBusinessDays" />
               </Grid>
            </Grid>
         </Panel>
         <Panel name={i18n.t('Holidays')}>
            <div style={{height: 500, width: '100%'}}>
               <DataGridPro
                  density="compact"
                  rows={holidaysWithId}
                  columns={holidayColumns}
                  pageSize={10}
                  disableMultipleSelection
                  onSelectionModelChange={(selectionModel) =>
                     setSelected(
                        holidaysWithId.find((h) => h.id === selectionModel[0]) as IHolidayWithId
                     )
                  }
               />
            </div>
            <Grid container spacing={3} justifyContent="space-around">
               <Grid item>
                  <PrimaryButton
                     type="button"
                     onClick={() => addOpen(true)}
                     style={{marginRight: 8}}
                  >
                     <Trans>Add</Trans>
                  </PrimaryButton>
                  <Button type="button" onClick={() => addUSOpen(true)}>
                     <Trans>Add Us Holiday</Trans>
                  </Button>
               </Grid>
               <Grid item>
                  <WarningButton
                     type="button"
                     onClick={deleteHoliday}
                     disabled={!selected}
                     style={{marginRight: 8}}
                  >
                     <Trans>Delete</Trans>
                  </WarningButton>
                  <WarningButton type="button" onClick={deletePast}>
                     <Trans>Delete Past Holidays</Trans>
                  </WarningButton>
               </Grid>
            </Grid>
         </Panel>
         <FormFooterButtons cancel={onClose} />
         {isAddOpened ? (
            <AddHolidayDialog onClose={() => addOpen(false)} onSubmit={addHoliday} />
         ) : null}
         {isAddUSOpened ? (
            <AddUSHolidayDialog onClose={() => addUSOpen(false)} onSubmit={addHoliday} />
         ) : null}
      </>
   );
};

const AddEditCalendarDialog = (props: Props) => {
   const {onClose, onSubmit, calendar} = props;

   return (
      <DialogComponent
         maxWidth="lg"
         header={
            calendar ? DialogTypes.EditCalendar.dialogName : DialogTypes.AddCalendar.dialogName
         }
         close={onClose}
      >
         <Formik
            initialValues={HolidayCalendar.cast(calendar) as IHolidayCalendar}
            validationSchema={HolidayCalendar}
            onSubmit={onSubmit}
         >
            <AddEditCalendarForm onClose={onClose} />
         </Formik>
      </DialogComponent>
   );
};

export default React.memo(AddEditCalendarDialog);
